// Global animation-speed multiplier, exposed as the CSS variable
// `--doi-anim-speed`. The admin slider in VolumePanel writes here;
// CSS keyframes (the keyword-strip fade) and the Motion-driven battle
// FX (zone moves, fizzle, trap flip, lunge, hover beat) both divide
// their base durations by the multiplier so a single knob slows
// everything in lock-step.

const VAR_NAME = "--doi-anim-speed";
const STORAGE_KEY = "doiAnimSpeed";
// 0.025x = 40x slowdown, enough wall-clock to screenshot each phase by hand.
const MIN = 0.025;
const MAX = 2;
const DEFAULT = 1;

const listeners = new Set<() => void>();

function clamp(v: number): number {
  if (!Number.isFinite(v)) return DEFAULT;
  if (v < MIN) return MIN;
  if (v > MAX) return MAX;
  return v;
}

function readPersisted(): number {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null) return DEFAULT;
    return clamp(Number(raw));
  } catch {
    return DEFAULT;
  }
}

let _speed = typeof window === "undefined" ? DEFAULT : readPersisted();

function writeRoot(speed: number) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(VAR_NAME, String(speed));
}

if (typeof window !== "undefined") {
  writeRoot(_speed);
}

export function getAnimSpeed(): number {
  return _speed;
}

export function setAnimSpeed(v: number): void {
  const next = clamp(v);
  if (next === _speed) return;
  _speed = next;
  writeRoot(next);
  try {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // ignore
  }
  for (const l of listeners) l();
}

export function subscribeAnimSpeed(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Wall-clock duration for a base ms at the current speed setting. */
export function effectiveDurationMs(baseMs: number): number {
  return Math.max(1, Math.round(baseMs / _speed));
}

export const ANIM_SPEED_BOUNDS = { min: MIN, max: MAX, default: DEFAULT };
